import { FieldError, Input, TextField } from "@heroui/react";
import { Icon } from "@iconify/react";
import { memo, useState } from "react";
import { StepHeading } from "@funnel-ui/primitives/StepHeading";

type CountryCode = {
  code: string;
  dial: string;
  flag: string;
};

const DEFAULT_COUNTRIES: CountryCode[] = [
  { code: "US", dial: "+1", flag: "🇺🇸" },
  { code: "CA", dial: "+1", flag: "🇨🇦" },
  { code: "UK", dial: "+44", flag: "🇬🇧" },
  { code: "FR", dial: "+33", flag: "🇫🇷" },
  { code: "DE", dial: "+49", flag: "🇩🇪" },
  { code: "AU", dial: "+61", flag: "🇦🇺" },
  { code: "JP", dial: "+81", flag: "🇯🇵" },
  { code: "BR", dial: "+55", flag: "🇧🇷" },
];

type PhoneStepProps = {
  question?: string;
  description?: string;
  initialCountry?: string;
  initialNumber?: string;
  countries?: CountryCode[];
  showTrustBadge?: boolean;
  onChange?: (e164: string) => void;
  onValidityChange?: (valid: boolean) => void;
  className?: string;
  testId?: string;
};

const stripPhone = (raw: string): string => raw.replace(/[^\d]/g, "");
const isValidPhone = (digits: string): boolean => digits.length >= 6 && digits.length <= 15;

const PhoneStepImpl = ({
  question = "What's your phone number?",
  description = "We'll only text you about important account updates.",
  initialCountry = "US",
  initialNumber = "",
  countries = DEFAULT_COUNTRIES,
  showTrustBadge = true,
  onChange,
  onValidityChange,
  className,
  testId,
}: PhoneStepProps) => {
  const [country, setCountry] = useState(initialCountry);
  const [number, setNumber] = useState(initialNumber);

  const selected = countries.find((c) => c.code === country) ?? countries[0]!;
  const digits = stripPhone(number);
  const valid = isValidPhone(digits);
  const error = number.length > 0 && !valid ? "Enter a valid number" : undefined;

  const handleNumber = (next: string): void => {
    setNumber(next);
    const e164 = `${selected.dial}${stripPhone(next)}`;
    onChange?.(e164);
    onValidityChange?.(isValidPhone(stripPhone(next)));
  };

  const handleCountry = (code: string): void => {
    setCountry(code);
    const next = countries.find((c) => c.code === code) ?? countries[0]!;
    onChange?.(`${next.dial}${digits}`);
  };

  return (
    <section data-testid={testId} className={`flex flex-col gap-5 ${className ?? ""}`}>
      <StepHeading title={question} description={description} editablePath="question" />
      <div className="flex gap-2">
        <select
          aria-label="Country"
          value={country}
          onChange={(event) => handleCountry(event.target.value)}
          className="rounded-xl border px-2 text-sm"
          style={{
            borderColor: "hsl(var(--brand-bg-foreground) / 0.15)",
            background: "hsl(var(--brand-bg-foreground) / 0.04)",
          }}
        >
          {countries.map((c) => (
            <option key={c.code} value={c.code}>
              {c.flag} {c.dial}
            </option>
          ))}
        </select>
        <TextField value={number} onChange={handleNumber} isInvalid={!!error} className="flex-1">
          <Input type="tel" inputMode="tel" autoComplete="tel" placeholder="555 123 4567" />
          {error && <FieldError>{error}</FieldError>}
        </TextField>
      </div>

      {showTrustBadge && (
        <div
          className="flex items-center gap-2 rounded-xl px-3 py-2 text-xs"
          style={{
            background: "hsl(var(--hero-success) / 0.08)",
            color: "hsl(var(--hero-success))",
          }}
        >
          <Icon icon="gravity-ui:lock" width={14} height={14} />
          <span>We never share your number.</span>
        </div>
      )}
    </section>
  );
};

export const PhoneStep = memo(PhoneStepImpl);

export default PhoneStep;
